import { usarUrl } from "@/composable/UsarUrl/usarUrl"
import { Erro } from "@/repositorios/erros"
import { ResultadoApiGET } from "@/types/api"

export type Repositorio<R, P = any, C = any> = (parametros: P, cabecalhos: C) => Promise<ResultadoApiGET<R>>
type FuncaoResultante<R> = (resultado: R) => void;
type Resolver<R> = { sucesso: FuncaoResultante<R>, erro?: FuncaoResultante<LeitorErro>, vazio?: FuncaoResultante<R>}
export interface LeitorErro {
  status: "erro"
  mensagem: string
  mensagemParaQuem: "usuario" | "dev"
}
export interface LeitorPendente {
  status: "pendente";
}
export interface LeitorSucesso<T = any> {
  status: "sucesso";
  dados: T[]
}
export interface LeitorVazio {
  status: "vazio"
}
export interface LeitorNegado {
  status: "negado"
}
export interface LeitorCancelado {
  status: "cancelado"
}
export type LeitorEstados<T = any> = (
  | LeitorErro
  | LeitorPendente
  | LeitorSucesso<T>
  | LeitorVazio
  | LeitorNegado
  | LeitorCancelado
)

const { pegarQueryStringDaRota } = usarUrl()

export abstract class UsarLeitorDeDados<R, P = any, C = any> {
  resultado: LeitorEstados<R>
  repositorio: Repositorio<R, P, C>
  itensPorPagina: number
  cabecalhoAdicionados: any
  parametrosBase: any
  parametrosIniciais: any
  parametrosTemporarios: any
  resultadosNestaPagina = 0
  totalDeResultados = 0
  consultaPaginaAnterior = ""
  consultaProximaPagina = ""
  consultaPrimeiraPagina = ""
  consultaUltimaPagina = ""

  constructor(repositorio: Repositorio<R, P, C>, parametros?: P, cabecalhos?: C) {
    this.resultado = {
      status: "pendente"
    }
    this.repositorio = repositorio
    this.parametrosBase = parametros || {}
    this.parametrosIniciais = parametros || {}
    this.parametrosTemporarios = {}
    this.cabecalhoAdicionados = cabecalhos || {}
    this.itensPorPagina = this.inicializarItensPorPagina(parametros)
  }

  inicializarItensPorPagina = (parametros): number => {
    const valorPadrao = 10
    if (!parametros) { return valorPadrao }
    const porPagina = parametros["por-pagina"]
    return porPagina ? parseInt(porPagina) : valorPadrao
  }

  sobrescreverParametros(novoParametro: P): void {
    this.parametrosBase = { ...novoParametro }
  }
  sobrescreverCabecalhos(novoCabecalho: C): void {
    this.cabecalhoAdicionados = { ...novoCabecalho }
  }
  incrementarParametros(novoParametro: P): void {
    this.resetarParametrosParaEstadoInicial()
    this.parametrosTemporarios = novoParametro
  }

  resetarParametrosParaEstadoInicial(): void {
    this.parametrosBase = this.parametrosIniciais
    this.parametrosTemporarios = {}
  }

  removerParametros(parametros: string[]): void {
    parametros.forEach(parametro => {
      delete this.parametrosBase[parametro]
    })
  }

  mudarPorPaginaNosParametros(novoPorPagina: string): void {
    this.parametrosBase["por-pagina"] = novoPorPagina
  }

  porPagina(porPagina: number): this {
    this.itensPorPagina = porPagina
    this.mudarPorPaginaNosParametros(porPagina.toString())
    return this
  }

  atualizarParametro(nomeParam: string, operador: string, valor: string): void {
    this.parametrosBase[nomeParam] = `${operador}.${valor}`
  }

  obterParametros() {
    return {
      ...this.parametrosBase,
      ...this.parametrosTemporarios
    }
  }

  async fazerRequisicaoUsandoRepositorio(parametros: P, cabecalhos: C): Promise<R[]> {
    const {
      resultados,
      resultadosNessaPagina,
      rotaPaginaAnterior,
      rotaProximaPagina,
      rotaPrimeiraPagina,
      rotaUltimaPagina,
      totalDeResultados
    } = await this.repositorio(parametros, cabecalhos)

    this.resultadosNestaPagina = resultadosNessaPagina
    this.consultaPaginaAnterior = pegarQueryStringDaRota(rotaPaginaAnterior)
    this.consultaProximaPagina = pegarQueryStringDaRota(rotaProximaPagina)
    this.consultaPrimeiraPagina = pegarQueryStringDaRota(rotaPrimeiraPagina)
    this.consultaUltimaPagina = pegarQueryStringDaRota(rotaUltimaPagina)
    this.totalDeResultados = totalDeResultados

    return resultados
  }

  async executar(): Promise<this> {
    try {
      this.mudarEstado("pendente")
      const resultados = await this.fazerRequisicaoComParametros()
      this.processarResultados(resultados)
    } catch (erro) {
      this.tratarErroDeExecutor(erro)
    }

    return this
  }

  fazerRequisicaoComParametros(): Promise<R[]> {
    const parametros = this.obterParametros()
    const cabecalhos = this.cabecalhoAdicionados
    return this.fazerRequisicaoUsandoRepositorio(parametros, cabecalhos)
  }

  processarResultados(resultados: R[]): void {
    const possuiDados = resultados.length > 0
    if (possuiDados) {
      this.mudarEstado({
        status: "sucesso",
        dados: resultados
      })
    } else {
      this.mudarEstado("vazio")
    }
  }

  tratarErroDeExecutor(erro: any): void {
    const erroCapturado = erro as Erro
    if (erroCapturado.codigo === 403) {
      this.mudarEstado("negado")
    } else if (erroCapturado.codigo === "cancelado") {
      this.mudarEstado("cancelado")
    } else {
      this.mudarEstado({
        status: "erro",
        mensagem: erroCapturado.mensagem,
        mensagemParaQuem: "usuario"
      })
    }
  }

  mudarEstado(resultado?: LeitorEstados<R>['status'] | LeitorEstados<R>): void {
    if (typeof resultado === "string") {
      this.resultado.status = resultado
    } else {
      this.resultado = resultado as LeitorEstados<R>
    }
  }

  resolver({ sucesso, erro, vazio }: Resolver<R[]>): void {
    if (this.resultado.status === "sucesso") {
      sucesso(this.resultado.dados)
    }

    if (this.resultado.status === "vazio" && vazio) {
      vazio([])
    }

    if (this.resultado.status === "erro" && erro) {
      erro(this.resultado)
    }
  }
}
